import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { saveExhibitors } from '../../../Reducer/keyExhibitorSlice';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useParams } from 'react-router-dom';
import UpdateKeyExhibitors from './UpdateKeyExhibitors';
import {
  Button,
  Grid,
  TextField,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Box,
  Typography,
  Paper,
  Divider,
  Tooltip
} from '@mui/material';
import { Add, Remove, Save, CloudUpload, ArrowBack } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Shadow styles
const shadowStyle = {
  boxShadow:
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
  transition: 'box-shadow 0.3s ease-in-out',
  '&:hover': {
    boxShadow:
      '0 12px 20px -3px rgba(0, 0, 0, 0.2), 0 6px 8px -4px rgba(0, 0, 0, 0.15)',
  },
};

const cardHoverShadowStyle = {
  boxShadow:
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    boxShadow:
      '0 12px 25px -5px rgba(0, 0, 0, 0.25), 0 8px 12px -6px rgba(0, 0, 0, 0.2)',
    transform: 'scale(1.02)',
  },
};

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

function AddKeyExhibitors() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [exhibitors, setExhibitors] = useState([
    {
      id: Date.now(),
      name: '',
      img: null,
      imgPreview: '',
      description: '',
      woocommerce_product_id: id,
    },
  ]);

  const handleInputChange = (index, event) => {
    const { name, value, files } = event.target;

    if (name === 'img' && files && files[0]) {
      const file = files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setExhibitors((prev) => {
          const updated = [...prev];
          updated[index].img = file;
          updated[index].imgPreview = reader.result;
          return updated;
        });
      };
      reader.readAsDataURL(file);
    } else {
      setExhibitors((prev) => {
        const updated = [...prev];
        updated[index][name] = value;
        return updated;
      });
    }
  };

  const addExhibitor = () => {
    setExhibitors((prev) => [
      ...prev,
      {
        id: Date.now(),
        name: '',
        img: null,
        imgPreview: '',
        description: '',
        woocommerce_product_id: id,
      },
    ]);
  };

  // ✅ Check if all required fields are filled
  const isFormValid = () => {
    return exhibitors.every(
      (ex) =>
        ex.name.trim() !== '' &&
        ex.description.trim() !== '' &&
        ex.img !== null
    );
  };

  const saveAllExhibitors = async () => {
    if (!isFormValid()) {
      toast.error('Please fill all exhibitor details before saving.');
      return;
    }

    try {
      await dispatch(saveExhibitors(exhibitors)).unwrap();
      toast.success('Key Exhibitors saved successfully!');
      navigate('/dashboard/exhibitor-productlist');
    } catch (err) {
      toast.error(err || 'Failed to save exhibitors. Please try again.');
    }
  };

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2, md: 3 }, 
      mb: { xs: '70%', sm: 0 }
    }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mb: 3, backgroundColor: 'transparent', ...shadowStyle }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 2,
            gap: 2,
          }}
        >
          <Typography variant="h5" component="h1" color="primary">
            Add Key Exhibitors
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/dashboard/exhibitor-productlist')}
          >
            Back to List
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Add one or more exhibitors to this product. Fill in the details and upload images for each exhibitor.
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        {exhibitors.map((exhibitor, index) => (
          <Grid item xs={12} sm={6} md={4} key={exhibitor.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                ...cardHoverShadowStyle,
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Exhibitor #{index + 1}
                </Typography>

                <TextField
                  fullWidth
                  label="Exhibitor Name"
                  name="name"
                  value={exhibitor.name}
                  onChange={(e) => handleInputChange(index, e)}
                  margin="normal"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />

                <Box sx={{ mb: 2 }}>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<CloudUpload />}
                    fullWidth
                  >
                    Upload Image
                    <VisuallyHiddenInput
                      type="file"
                      name="img"
                      accept="image/*"
                      onChange={(e) => handleInputChange(index, e)}
                    />
                  </Button>
                </Box>

                {exhibitor.imgPreview && (
                  <Box
                    sx={{
                      mb: 2,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <Avatar
                      src={exhibitor.imgPreview}
                      alt="Preview"
                      sx={{
                        width: { xs: 80, sm: 100, md: 120 },
                        height: { xs: 80, sm: 100, md: 120 },
                        variant: 'rounded',
                      }}
                    />
                  </Box>
                )}

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  name="description"
                  value={exhibitor.description}
                  onChange={(e) => handleInputChange(index, e)}
                  margin="normal"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="WooCommerce Product ID"
                  name="woocommerce_product_id"
                  value={exhibitor.woocommerce_product_id}
                  InputProps={{ readOnly: true }}
                  margin="normal"
                  variant="outlined"
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper
        elevation={3}
        sx={{
          p: 2,
          mt: 3,
          width: { xs: '100%', sm: '80%', md: '50%' },
          mx: 'auto',
        }}
      >
        <Grid
          container
          spacing={2}
          justifyContent="center"
          alignItems="center"
        >
          <Grid item>
            <Tooltip title="Add Exhibitor">
              <IconButton
                color="primary"
                onClick={addExhibitor}
                size="large"
                sx={{
                  backgroundColor: 'primary.light',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'blue',
                  },
                }}
              >
                <Add fontSize="large" />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item>
            <Tooltip title="Remove Last Exhibitor">
              <IconButton
                color="secondary"
                onClick={() => setExhibitors(exhibitors.slice(0, -1))}
                size="large"
                sx={{
                  backgroundColor: 'secondary.light',
                  '&:hover': {
                    backgroundColor: 'secondary.main',
                    color: 'violet',
                  },
                }}
                disabled={exhibitors.length <= 1}
              >
                <Remove fontSize="large" />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="contained"
            color="success"
            onClick={saveAllExhibitors}
            startIcon={<Save />}
            size="large"
            disabled={!isFormValid()}
            sx={{
              px: { xs: 2, sm: 4 },
              py: { xs: 1, sm: 1.5 },
              fontSize: { xs: '0.8rem', sm: '1rem' },
            }}
          >
            Save All Exhibitors
          </Button>
        </Box>
      </Paper>

      <UpdateKeyExhibitors />
    </Box>
  );
}

export default AddKeyExhibitors;
